import { AlertCircle, CheckCircle2, ChevronRight, Info, Plus, TriangleAlert, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';

export function Skeleton({ width = '100%', height = '1rem', label = 'Loading' }: { width?: string; height?: string; label?: string }) {
  return <span className="bv-skeleton" style={{ width, height }} role="status" aria-label={label} />;
}

/** Full-page loading state for session bootstrap, shaped like the app shell it's about to become
 * (sidebar + topbar + page heading + content rows) rather than a centred spinner, so the layout doesn't
 * visibly jump once the real content arrives. */
export function PageLoading({ label = 'Loading Boothiva…' }: { label?: string }) {
  return <div className="bv-page-loading" role="status" aria-label={label}>
    <div className="bv-page-loading__sidebar">
      <span className="bv-skeleton bv-page-loading__brand" />
      {Array.from({ length: 7 }, (_, index) => <span key={index} className="bv-skeleton bv-page-loading__nav-item" />)}
    </div>
    <div className="bv-page-loading__main">
      <div className="bv-page-loading__topbar">
        <span className="bv-skeleton" style={{ width: '12rem', height: '1rem' }} />
        <span className="bv-skeleton" style={{ width: '6rem', height: '2rem' }} />
      </div>
      <div className="bv-page-loading__content">
        <span className="bv-skeleton" style={{ width: '11rem', height: '2rem' }} />
        <span className="bv-skeleton" style={{ width: '22rem', height: '1rem' }} />
        <div className="bv-page-loading__cards">
          {Array.from({ length: 3 }, (_, index) => <span key={index} className="bv-skeleton bv-page-loading__card" />)}
        </div>
        <span className="bv-skeleton bv-page-loading__panel" />
      </div>
    </div>
  </div>;
}

export function StatCard({ label, value, delta, trend = 'up', sparkline, loading = false, icon, href }: {
  label: string; value: string; delta?: string; trend?: 'up' | 'down'; sparkline?: ReactNode; loading?: boolean; icon?: ReactNode; href?: string;
}) {
  const body = loading ? <><Skeleton width="45%" /><Skeleton height="2rem" /><Skeleton width="65%" /></> : <>
    {/* The chevron marks the card as somewhere to go. Only on the linked variant, because on a plain
        statistic it would promise a destination that isn't there. */}
    <div className="bv-stat-card__head">{icon && <span className="bv-stat-card__icon" aria-hidden="true">{icon}</span>}<span className="bv-muted">{label}</span>
      {href && <ChevronRight aria-hidden="true" className="bv-stat-card__go" size={18} />}</div>
    <span className="bv-stat-card__value">{value}</span>
    <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
      {delta && <span className={`bv-stat-card__delta--${trend}`}>{trend === 'up' ? <TrendingUp aria-hidden="true" size={16} /> : <TrendingDown aria-hidden="true" size={16} />} {delta}</span>}
      {sparkline}
    </div></>;
  if (href && !loading) return <a className="bv-surface bv-stat-card bv-stat-card--link" aria-label={label} href={href}>{body}</a>;
  return <article className="bv-surface bv-stat-card" aria-label={label}>{body}</article>;
}

type StatusTone = 'success' | 'warning' | 'danger' | 'info';
const statusIcons = { success: CheckCircle2, warning: TriangleAlert, danger: AlertCircle, info: Info };
export function StatusBadge({ text, tone = 'info' }: { text: string; tone?: StatusTone }) {
  const Icon = statusIcons[tone];
  return <span className={`bv-status bv-status--${tone}`}><Icon aria-hidden="true" size={14} /><span>{text}</span></span>;
}

export type TimelineItem = { id: string; title: string; description?: string; timestamp: string; day?: string };
export function Timeline({ items }: { items: TimelineItem[] }) {
  return <ol className="bv-timeline">{items.map((item) => <li key={item.id}>
    {item.day && <strong>{item.day}</strong>}<span>{item.title}</span>{item.description && <span className="bv-muted">{item.description}</span>}
    <time className="bv-muted" dateTime={item.timestamp} title={new Date(item.timestamp).toLocaleString()}>{new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
  </li>)}</ol>;
}

export function EmptyState({ title, description, actionLabel = 'Create your first event', onAction, actionDisabled = false }: { title: string; description: string; actionLabel?: string; onAction?: () => void; actionDisabled?: boolean }) {
  return <div className="bv-empty"><Info aria-hidden="true" size={32} /><h3>{title}</h3><p className="bv-muted">{description}</p>{(onAction || actionDisabled) && <button className="bv-button" type="button" onClick={onAction} disabled={actionDisabled}><Plus aria-hidden="true" size={18} />{actionLabel}</button>}</div>;
}
